import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Divider, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import NewAppBar from 'components/Navbars/AppBar';
import NotificationCard from './NotificationCard';
import Footer from 'views/Landing/Footer';
import useSignalingStore from 'views/signalingStore';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import colors from 'colors/color';

const PageContainer = styled(Container)(({ theme }) => ({
  maxWidth: '768px',
  padding: theme.spacing(4, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1),
  },
}));

const NotificationTitle = styled(Typography)(({ theme }) => ({
  color: '#0017e7',
  fontWeight: 500,
  marginBottom: theme.spacing(3),
  fontSize: '40px',
  fontFamily: 'Poppins',
  [theme.breakpoints.down('sm')]: {
    fontSize: '32px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#0000004D',
  fontWeight: 500,
  marginBottom: theme.spacing(3),
  fontSize: '24px',
  fontFamily: 'Poppins',
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
  },
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '2rem',
});

export default function NotificationsPage() {
  const {
    notifications,
    fetchUnreadNotifications,
    markNotificationAsRead,
    markNotificationsAsRead,
  } = useSignalingStore();
  
  const [loading, setLoading] = useState(true);
  const [groupedNotifications, setGroupedNotifications] = useState({});

  useEffect(() => {
    console.log('notifs:',notifications);
  },[])
  useEffect(() => {
    // Fetch notifications when component mounts
    const loadNotifications = async () => {
      try {
        await fetchUnreadNotifications();
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
        setLoading(false);
      }
    };

    loadNotifications();
    
    // Mark all notifications as read when the page is viewed
    // return () => {
    //   markNotificationsAsRead();
    // };
  }, [fetchUnreadNotifications]);

  useEffect(() => {
    // Group notifications by date
    const groupByDate = () => {
      const groups = {};
      
      if (notifications && notifications.length > 0) {
        notifications.forEach(notification => {
          // Get the date part only
          const date = new Date(notification.timestamp);
          const dateString = date.toDateString();
          
          // Check if it's today
          const today = new Date().toDateString();
          const displayDate = dateString === today ? 'Today' : formatDate(date);
          
          if (!groups[displayDate]) {
            groups[displayDate] = [];
          }
          
          groups[displayDate].push(notification);
        });
      }
      
      // Sort dates with "Today" at the top
      const sortedGroups = {};
      if (groups['Today']) {
        sortedGroups['Today'] = groups['Today'];
      }
      
      // Add other dates in descending order
      Object.keys(groups)
        .filter(date => date !== 'Today')
        .sort((a, b) => new Date(b) - new Date(a))
        .forEach(date => {
          sortedGroups[date] = groups[date];
        });
        
      setGroupedNotifications(sortedGroups);
    };
    
    groupByDate();
  }, [notifications]);

  // Format date as "DD Month YYYY"
  const formatDate = (date) => {
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Handle clicking on a notification
  const handleNotificationClick = (notificationId) => {
    markNotificationAsRead(notificationId);
  };

  if (loading) {
    return (
      <Box>
        <NewAppBar />
        <PageContainer>
          <NotificationTitle>Notifications</NotificationTitle>
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        </PageContainer>
        <Footer />
      </Box>
    );
  }

  return (
    <Box>
      <NewAppBar />
      <PageContainer>
        <NotificationTitle>Notifications</NotificationTitle>
        <Box sx={{ '& > div': { marginBottom: 3 } }}>
          {Object.keys(groupedNotifications).length > 0 ? (
            Object.entries(groupedNotifications).map(([date, dateNotifications]) => (
              <Box key={date}>
                <SectionTitle>{date}</SectionTitle>
                <Divider sx={{ borderColor: '#d9d9d9' }} />
                {dateNotifications.map(notification => (
                  <NotificationCard 
                    key={notification.id}
                    notification={notification}
                    isRead={notification.read}
                    markNotificationsAsRead={markNotificationsAsRead}
                    onClick={() => handleNotificationClick(notification.id)}
                  />
                ))}
              </Box>
            ))
          ) : (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 6,
                px: 2
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  border: `0.5px solid ${colors.primary}`,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <NotificationsOffIcon 
                  sx={{ 
                    fontSize: 40,
                    color: colors.primary
                  }} 
                />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Poppins', 
                  fontWeight: 600,
                  color: colors.primary,
                  mb: 1
                }}
              >
                No notifications yet
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: 'Poppins', 
                  color: '#00000080',
                  textAlign: 'center',
                  maxWidth: 400
                }}
              >
                When you receive notifications about your activity, they will appear here
              </Typography>
            </Box>
          )}
        </Box>
      </PageContainer>
      <Footer />
    </Box>
  );
}